import { useEffect, useRef, useState } from "react";

/**
 * Animate a number from 0 -> target when the element enters the viewport.
 * Returns [ref, currentValue].
 */
export function useCountUp(target, { duration = 1600, delay = 0 } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now() + delay;
            const from = 0;
            const to = target;
            const step = (now) => {
              const t = Math.min(1, Math.max(0, (now - start) / duration));
              // easeOutExpo
              const eased =
                t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
              setValue(Math.round(from + (to - from) * eased));
              if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [target, duration, delay]);

  return [ref, value];
}
